import {
  ArrowDown,
  ArrowUp,
  DotsSixVertical,
  Key,
  Plus,
  Scales,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCurrentWorkspace, useWorkspaces } from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import {
  describeLimits,
  type FallbackHop,
  type GrantLimits,
  type GrantRow,
  type ThinkerRecord,
  useEconomyAction,
  useFallbacks,
  useGrants,
  useProviderKeys,
  useThinkerRegistry,
  useThinkerUsage,
} from "~/features/settings/sections/thinkers/economy/economyApi";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { BudgetsPanel } from "./BudgetsPanel";

/**
 * BRONCO Opus 02 — the Thinkers page's economy tabs. A thinker is OWNED by a team, SERVED by hubs and USED only under a grant
 * (a standing grant, a timed lease, a borrow that was asked for, or the estate pool). Everything shown here is what the server
 * decided; the buttons only ask — the bus refuses by name what a grant does not cover.
 */

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "—");

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function useNotify() {
  const { t } = useTranslation();
  return {
    ok: (message: string) => toast.success(message),
    fail: (error: unknown) => toast.error(errorText(error) || t("That did not work.")),
  };
}

function hubsLine(
  record: ThinkerRecord,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  const live = record.hubs.filter((hub) => hub.live);
  if (live.length === 0) return t("no live hub");
  return live.length === 1
    ? t("served by {{hub}}", { hub: live[0]?.name })
    : t("{{n}} hubs online", { n: live.length });
}

function whoHolds(grant: GrantRow, t: (key: string, options?: Record<string, unknown>) => string) {
  if (grant.recipientUserName) return grant.recipientUserName;
  if (grant.recipientRole) return t("role {{role}} in a project", { role: grant.recipientRole });
  if (grant.recipientProjectId) return t("a project");
  return grant.recipientTeamName ?? t("a team");
}

// ── Mine ───────────────────────────────────────────────────────────────────────

export function MinePanel({ canAdminister }: { canAdminister: boolean }) {
  const { t } = useTranslation();
  const registry = useThinkerRegistry();
  const grants = useGrants("owner");
  const own = useEconomyAction<{ thinkerId: string; pooled: boolean }>("thinkers.own.set");
  const revoke = useEconomyAction<{ id: string }>("thinkers.grant.revoke");
  const notify = useNotify();
  const [lending, setLending] = useState<string | null>(null);

  if (registry.isLoading) return <Spinner label={t("Loading")} />;
  if (registry.error) return <Text variant="small">{errorText(registry.error)}</Text>;
  const mine = (registry.data ?? []).filter((record) => record.section === "mine");

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "The thinkers your workspace owns. Put one in the estate pool and any other workspace may use it at low priority; lend it to give a team more.",
        )}
      </Text>
      {mine.length === 0 ? (
        <Empty
          icon={Scales}
          message={t("Your workspace owns no thinkers yet — a hub you run gives it some.")}
        />
      ) : (
        <Rows>
          {mine.map((record) => {
            const holders = (grants.data ?? []).filter(
              (grant) => grant.thinkerId === record.thinkerId && grant.status === "active",
            );
            return (
              <Row key={record.thinkerId} className="flex-col items-stretch gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body text-text">{record.label}</p>
                    <p className="truncate text-small text-text-tertiary">
                      {hubsLine(record, t)} ·{" "}
                      {t("{{tokens}} tokens in {{n}} calls today", {
                        tokens: record.tokensToday.toLocaleString(),
                        n: record.requestsToday,
                      })}
                    </p>
                  </div>
                  <Pill>{t(record.kind)}</Pill>
                  {record.pooled ? <Pill tone="accent">{t("pooled")}</Pill> : null}
                </div>
                {canAdminister ? (
                  <Switch
                    checked={record.pooled}
                    label={t("In the estate pool")}
                    description={
                      record.pooled
                        ? t("Any other workspace may use it: {{limits}}.", {
                            limits: describeLimits(
                              record.poolLimits ?? { priorityCeiling: 3, slots: 1 },
                              t,
                            ),
                          })
                        : t("Only your workspace and those you lend it to.")
                    }
                    disabled={own.isPending}
                    onCheckedChange={(pooled) =>
                      own.mutate({ thinkerId: record.thinkerId, pooled }, { onError: notify.fail })
                    }
                  />
                ) : null}
                {holders.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    <p className="text-small text-text-secondary">{t("Who has it")}</p>
                    {holders.map((grant) => (
                      <div key={grant.id} className="flex items-center gap-2 text-small text-text">
                        <span className="min-w-0 flex-1 truncate">
                          {whoHolds(grant, t)} · {t(grant.mode)} · {describeLimits(grant.limits, t)}
                          {grant.expiresAt
                            ? ` · ${t("until {{date}}", { date: day(grant.expiresAt) })}`
                            : ""}
                        </span>
                        {canAdminister ? (
                          <IconButton
                            icon={Trash}
                            size="sm"
                            aria-label={t("Revoke")}
                            disabled={revoke.isPending}
                            onClick={() =>
                              revoke.mutate(
                                { id: grant.id },
                                {
                                  onSuccess: () =>
                                    notify.ok(t("Revoked — its running calls were stopped")),
                                  onError: notify.fail,
                                },
                              )
                            }
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                {canAdminister ? (
                  lending === record.thinkerId ? (
                    <LendForm record={record} onDone={() => setLending(null)} />
                  ) : (
                    <div>
                      <Button size="sm" icon={Plus} onClick={() => setLending(record.thinkerId)}>
                        {t("Lend")}
                      </Button>
                    </div>
                  )
                ) : null}
              </Row>
            );
          })}
        </Rows>
      )}
    </div>
  );
}

function LendForm({ record, onDone }: { record: ThinkerRecord; onDone: () => void }) {
  const { t } = useTranslation();
  const { data: workspaces } = useWorkspaces();
  const { data: current } = useCurrentWorkspace();
  const grant = useEconomyAction<Record<string, unknown>>("thinkers.grant");
  const notify = useNotify();
  const [team, setTeam] = useState("");
  const [days, setDays] = useState("30");
  const [priority, setPriority] = useState("5");
  const [slots, setSlots] = useState("2");
  const [tokens, setTokens] = useState("2000000");
  const [dataClass, setDataClass] = useState("internal");
  const others = (workspaces ?? []).filter((workspace) => workspace.id !== current?.id);
  const limits: GrantLimits = {
    priorityCeiling: Number(priority) || null,
    slots: Number(slots) || null,
    tokensPerDay: Number(tokens) || null,
    dataClass,
  };

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md bg-bg-secondary p-2">
      <Select
        label={t("To workspace")}
        value={team}
        className="min-w-48"
        onChange={(event) => setTeam(event.target.value)}
      >
        <option value="">{t("Choose a workspace")}</option>
        {others.map((workspace) => (
          <option key={workspace.id} value={workspace.id}>
            {workspace.name}
          </option>
        ))}
      </Select>
      <Input
        label={t("Days")}
        value={days}
        className="w-20"
        onChange={(event) => setDays(event.target.value)}
      />
      <Input
        label={t("Priority ≤")}
        value={priority}
        className="w-20"
        onChange={(event) => setPriority(event.target.value)}
      />
      <Input
        label={t("At once")}
        value={slots}
        className="w-20"
        onChange={(event) => setSlots(event.target.value)}
      />
      <Input
        label={t("Tokens a day")}
        value={tokens}
        className="w-32"
        onChange={(event) => setTokens(event.target.value)}
      />
      <Select
        label={t("Data")}
        value={dataClass}
        onChange={(event) => setDataClass(event.target.value)}
      >
        <option value="internal">{t("internal")}</option>
        <option value="customer">{t("customer")}</option>
        <option value="any">{t("any")}</option>
      </Select>
      <Button
        variant="primary"
        disabled={!team || grant.isPending}
        onClick={() =>
          grant.mutate(
            {
              thinkerId: record.thinkerId,
              mode: "lease",
              recipient: { teamId: team },
              limits,
              expiresAt: new Date(Date.now() + (Number(days) || 30) * 86_400_000).toISOString(),
            },
            {
              onSuccess: () => {
                notify.ok(t("Lent"));
                onDone();
              },
              onError: notify.fail,
            },
          )
        }
      >
        {t("Lend {{thinker}}", { thinker: record.label })}
      </Button>
      <Button variant="ghost" onClick={onDone}>
        {t("Cancel")}
      </Button>
    </div>
  );
}

// ── Shared with me ─────────────────────────────────────────────────────────────

export function SharedPanel({ requestFor }: { requestFor: string | null }) {
  const { t } = useTranslation();
  const registry = useThinkerRegistry();
  const [asking, setAsking] = useState<{
    thinkerId: string;
    label: string;
    widenedFrom?: string;
  } | null>(requestFor ? { thinkerId: requestFor, label: requestFor } : null);

  if (registry.isLoading) return <Spinner label={t("Loading")} />;
  if (registry.error) return <Text variant="small">{errorText(registry.error)}</Text>;
  const shared = (registry.data ?? []).filter((record) => record.section !== "mine");
  const requested = requestFor
    ? (registry.data ?? []).find((record) => record.thinkerId === requestFor)
    : undefined;

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "Thinkers other workspaces let you use — what you hold on each, until when, and within which limits.",
        )}
      </Text>
      {asking ? (
        <RequestForm
          thinkerId={asking.thinkerId}
          label={requested?.label ?? asking.label}
          widenedFrom={asking.widenedFrom}
          onDone={() => setAsking(null)}
        />
      ) : null}
      {shared.length === 0 ? (
        <Empty icon={Scales} message={t("Nothing is shared with this workspace yet.")} />
      ) : (
        <Rows>
          {shared.map((record) => (
            <Row key={record.thinkerId}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">
                  {record.label}{" "}
                  <span className="text-text-tertiary">
                    · {t("owned by {{team}}", { team: record.ownerTeamName })}
                  </span>
                </p>
                <p className="truncate text-small text-text-tertiary">
                  {record.myGrant
                    ? `${describeLimits(record.myGrant.limits, t)}${record.myGrant.expiresAt ? ` · ${t("until {{date}}", { date: day(record.myGrant.expiresAt) })}` : ""}`
                    : t("pool: priority ≤ 3, one at a time")}
                  {" · "}
                  {hubsLine(record, t)}
                </p>
              </div>
              <Pill tone={record.section === "pooled" ? "neutral" : "accent"}>
                {t(record.myGrant?.mode ?? "pool")}
              </Pill>
              <Button
                size="sm"
                onClick={() =>
                  setAsking({
                    thinkerId: record.thinkerId,
                    label: record.label,
                    widenedFrom: record.myGrant?.grantId ?? undefined,
                  })
                }
              >
                {t("Request more")}
              </Button>
            </Row>
          ))}
        </Rows>
      )}
    </div>
  );
}

function RequestForm({
  thinkerId,
  label,
  widenedFrom,
  onDone,
}: {
  thinkerId: string;
  label: string;
  widenedFrom?: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const request = useEconomyAction<Record<string, unknown>>("thinkers.request");
  const notify = useNotify();
  const [purpose, setPurpose] = useState("");
  const [hours, setHours] = useState("24");
  const [forTeam, setForTeam] = useState(false);

  return (
    <div className="flex flex-col gap-2 rounded-md bg-bg-secondary p-3">
      <p className="text-body text-text">{t("Ask to borrow {{thinker}}", { thinker: label })}</p>
      <Input
        label={t("What is it for?")}
        value={purpose}
        placeholder={t("e.g. a day of document distillation")}
        onChange={(event) => setPurpose(event.target.value)}
      />
      <div className="flex flex-wrap items-end gap-2">
        <Select
          label={t("For how long")}
          value={hours}
          onChange={(event) => setHours(event.target.value)}
        >
          <option value="1">{t("1 hour")}</option>
          <option value="8">{t("8 hours")}</option>
          <option value="24">{t("1 day")}</option>
          <option value="72">{t("3 days")}</option>
          <option value="168">{t("1 week")}</option>
          <option value="720">{t("30 days")}</option>
        </Select>
        <Switch
          checked={forTeam}
          label={t("For my whole workspace")}
          onCheckedChange={setForTeam}
        />
        <Button
          variant="primary"
          disabled={purpose.trim().length === 0 || request.isPending}
          onClick={() =>
            request.mutate(
              { thinkerId, purpose, hours: Number(hours), forTeam, widenedFrom },
              {
                onSuccess: () => {
                  notify.ok(t("Asked — the owner sees one question and answers it"));
                  onDone();
                },
                onError: notify.fail,
              },
            )
          }
        >
          {t("Ask the owner")}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}

// ── Leases ─────────────────────────────────────────────────────────────────────

export function LeasesPanel({ canAdminister }: { canAdminister: boolean }) {
  const { t } = useTranslation();
  const out = useGrants("owner");
  const incoming = useGrants("recipient");
  const revoke = useEconomyAction<{ id: string }>("thinkers.grant.revoke");
  const extend = useEconomyAction<{ id: string; until: string }>("thinkers.grant.extend");
  const notify = useNotify();

  if (out.isLoading || incoming.isLoading) return <Spinner label={t("Loading")} />;
  const rows = [
    ...(out.data ?? [])
      .filter((grant) => grant.status !== "pending")
      .map((grant) => ({ grant, direction: "out" as const })),
    ...(incoming.data ?? [])
      .filter((grant) => grant.status !== "pending")
      .map((grant) => ({ grant, direction: "in" as const })),
  ];

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "What your workspace lends and holds. Revoking stops the calls still running under it at once.",
        )}
      </Text>
      {rows.length === 0 ? (
        <Empty icon={Scales} message={t("No leases or grants yet.")} />
      ) : (
        <Rows>
          {rows.map(({ grant, direction }) => (
            <Row key={`${direction}-${grant.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">
                  {grant.label}{" "}
                  <span className="text-text-tertiary">
                    ·{" "}
                    {direction === "out"
                      ? t("to {{who}}", { who: whoHolds(grant, t) })
                      : t("from {{team}}", { team: grant.ownerTeamName })}
                  </span>
                </p>
                <p className="truncate text-small text-text-tertiary">
                  {t(grant.mode)} · {describeLimits(grant.limits, t)}
                  {grant.expiresAt
                    ? ` · ${t("until {{date}}", { date: day(grant.expiresAt) })}`
                    : ""}
                  {` · ${t("{{tokens}} tokens today", { tokens: grant.tokensToday.toLocaleString() })}`}
                </p>
              </div>
              <Pill
                tone={
                  grant.status === "active"
                    ? "success"
                    : grant.status === "expired"
                      ? "warning"
                      : "neutral"
                }
              >
                {t(grant.status)}
              </Pill>
              {grant.status === "active" && direction === "out" && canAdminister ? (
                <Button
                  size="sm"
                  disabled={extend.isPending}
                  onClick={() =>
                    extend.mutate(
                      {
                        id: grant.id,
                        until: new Date(
                          Math.max(
                            Date.now(),
                            grant.expiresAt ? new Date(grant.expiresAt).getTime() : Date.now(),
                          ) +
                            7 * 86_400_000,
                        ).toISOString(),
                      },
                      { onSuccess: () => notify.ok(t("Extended by a week")), onError: notify.fail },
                    )
                  }
                >
                  {t("+1 week")}
                </Button>
              ) : null}
              {grant.status === "active" ? (
                <IconButton
                  icon={Trash}
                  size="sm"
                  aria-label={t("Revoke")}
                  disabled={revoke.isPending}
                  onClick={() =>
                    revoke.mutate(
                      { id: grant.id },
                      {
                        onSuccess: () => notify.ok(t("Revoked — its running calls were stopped")),
                        onError: notify.fail,
                      },
                    )
                  }
                />
              ) : null}
            </Row>
          ))}
        </Rows>
      )}
    </div>
  );
}

// ── Requests ───────────────────────────────────────────────────────────────────

export function RequestsPanel({ canAdminister }: { canAdminister: boolean }) {
  const { t } = useTranslation();
  const out = useGrants("owner");
  const mine = useGrants("recipient");
  const approve = useEconomyAction<{ id: string }>("thinkers.grant.approve");
  const decline = useEconomyAction<{ id: string }>("thinkers.grant.decline");
  const notify = useNotify();

  if (out.isLoading || mine.isLoading) return <Spinner label={t("Loading")} />;
  const asked = (out.data ?? []).filter((grant) => grant.status === "pending");
  const waiting = (mine.data ?? []).filter((grant) => grant.status === "pending");
  const length = (hours: number | null) =>
    hours == null
      ? ""
      : hours % 24 === 0
        ? t("{{n}} day(s)", { n: hours / 24 })
        : t("{{n}} hour(s)", { n: hours });

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "People asking to borrow your thinkers. One line each; the same question is in your Docit Work inbox.",
        )}
      </Text>
      {asked.length === 0 ? (
        <Empty icon={Scales} message={t("Nobody is waiting for an answer.")} />
      ) : (
        <Rows>
          {asked.map((grant) => (
            <Row key={grant.id}>
              <p className="min-w-0 flex-1 text-body text-text">
                {t("{{who}} asks for {{thinker}} for {{length}}: “{{purpose}}”", {
                  who: grant.recipientUserName ?? grant.recipientTeamName ?? t("a team"),
                  thinker: grant.label,
                  length: length(grant.requestedHours),
                  purpose: grant.purpose ?? "",
                })}
              </p>
              {canAdminister ? (
                <>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={approve.isPending}
                    onClick={() =>
                      approve.mutate(
                        { id: grant.id },
                        { onSuccess: () => notify.ok(t("Lent")), onError: notify.fail },
                      )
                    }
                  >
                    {t("Approve")}
                  </Button>
                  <Button
                    size="sm"
                    disabled={decline.isPending}
                    onClick={() =>
                      decline.mutate(
                        { id: grant.id },
                        { onSuccess: () => notify.ok(t("Declined")), onError: notify.fail },
                      )
                    }
                  >
                    {t("Decline")}
                  </Button>
                </>
              ) : null}
            </Row>
          ))}
        </Rows>
      )}
      {waiting.length > 0 ? (
        <>
          <p className="text-small text-text-secondary">{t("You asked")}</p>
          <Rows>
            {waiting.map((grant) => (
              <Row key={grant.id}>
                <p className="min-w-0 flex-1 text-body text-text">
                  {t("{{thinker}} from {{team}} — waiting for the owner", {
                    thinker: grant.label,
                    team: grant.ownerTeamName,
                  })}
                </p>
                <Pill tone="warning">{t("pending")}</Pill>
              </Row>
            ))}
          </Rows>
        </>
      ) : null}
    </div>
  );
}

// ── Keys ───────────────────────────────────────────────────────────────────────

export function KeysPanel({ canAdminister }: { canAdminister: boolean }) {
  const { t } = useTranslation();
  const keys = useProviderKeys();
  const add = useEconomyAction<Record<string, unknown>>("thinkers.keys.set");
  const revoke = useEconomyAction<{ id: string }>("thinkers.keys.revoke");
  const notify = useNotify();
  const [provider, setProvider] = useState("anthropic");
  const [baseUrl, setBaseUrl] = useState("");
  const [label, setLabel] = useState("");
  const [secret, setSecret] = useState("");
  const [models, setModels] = useState("claude-haiku-5-5");

  if (keys.isLoading) return <Spinner label={t("Loading")} />;
  if (keys.error) return <Text variant="small">{errorText(keys.error)}</Text>;
  const plan = keys.data?.plan;

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "Your workspace's own provider keys. A key becomes provider thinkers (one per model) served by the provider hub; the key is sealed and opened only for the call that uses it.",
        )}
      </Text>
      {plan && !plan.allowsByoKey ? (
        <Text variant="small">
          {t(
            "Your plan ({{plan}}) does not include your own provider keys — the Business and Enterprise plans do.",
            {
              plan: plan.name,
            },
          )}
        </Text>
      ) : null}
      {(keys.data?.keys ?? []).length === 0 ? (
        <Empty icon={Key} message={t("No provider keys.")} />
      ) : (
        <Rows>
          {(keys.data?.keys ?? []).map((key) => (
            <Row key={key.id}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">
                  {key.label} <span className="text-text-tertiary">· {key.masked}</span>
                </p>
                <p className="truncate text-small text-text-tertiary">
                  {key.provider} · {key.models.join(", ")}
                  {key.lastUsedAt
                    ? ` · ${t("last used {{date}}", { date: day(key.lastUsedAt) })}`
                    : ""}
                </p>
              </div>
              <Pill tone={key.status === "active" ? "success" : "neutral"}>{t(key.status)}</Pill>
              {canAdminister && key.status === "active" ? (
                <IconButton
                  icon={Trash}
                  size="sm"
                  aria-label={t("Revoke")}
                  disabled={revoke.isPending}
                  onClick={() =>
                    revoke.mutate(
                      { id: key.id },
                      { onSuccess: () => notify.ok(t("Key revoked")), onError: notify.fail },
                    )
                  }
                />
              ) : null}
            </Row>
          ))}
        </Rows>
      )}
      {canAdminister ? (
        <div className="flex flex-wrap items-end gap-2">
          <Select
            label={t("Provider")}
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
          >
            <option value="anthropic">Anthropic</option>
            <option value="openai-compatible">{t("OpenAI-compatible")}</option>
          </Select>
          {provider === "openai-compatible" ? (
            <Input
              label={t("Base URL")}
              value={baseUrl}
              className="min-w-64"
              placeholder="https://openrouter.ai/api/v1"
              onChange={(event) => setBaseUrl(event.target.value)}
            />
          ) : null}
          <Input
            label={t("Label")}
            value={label}
            className="min-w-32"
            onChange={(event) => setLabel(event.target.value)}
          />
          <Input
            label={t("Key")}
            type="password"
            value={secret}
            className="min-w-48"
            autoComplete="off"
            onChange={(event) => setSecret(event.target.value)}
          />
          <Input
            label={t("Models (comma-separated)")}
            value={models}
            className="min-w-48"
            onChange={(event) => setModels(event.target.value)}
          />
          <Button
            variant="primary"
            icon={Plus}
            disabled={!secret || add.isPending}
            onClick={() =>
              add.mutate(
                {
                  provider,
                  baseUrl: provider === "openai-compatible" ? baseUrl : undefined,
                  label: label || provider,
                  key: secret,
                  models: models
                    .split(",")
                    .map((model) => model.trim())
                    .filter(Boolean),
                },
                {
                  onSuccess: () => {
                    notify.ok(t("Key added — its thinkers come online within a minute"));
                    setSecret("");
                  },
                  onError: notify.fail,
                },
              )
            }
          >
            {t("Add key")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ── Fallbacks ──────────────────────────────────────────────────────────────────

/** A hop being edited, with a stable id so reordering keeps each row's own state. */
type DraftHop = FallbackHop & { uid: string };

export function FallbacksPanel({ canAdminister }: { canAdminister: boolean }) {
  const { t } = useTranslation();
  const fallbacks = useFallbacks();
  const registry = useThinkerRegistry();
  const save = useEconomyAction<Record<string, unknown>>("thinkers.fallbacks.set");
  const notify = useNotify();
  const teamChain = fallbacks.data?.chains.find((chain) => chain.scope === "team");
  const [draft, setDraft] = useState<DraftHop[] | null>(null);
  const [dragged, setDragged] = useState<number | null>(null);

  if (fallbacks.isLoading || registry.isLoading) return <Spinner label={t("Loading")} />;
  if (fallbacks.error) return <Text variant="small">{errorText(fallbacks.error)}</Text>;
  const hops: DraftHop[] =
    draft ??
    (teamChain?.chain ?? []).map((hop, position) => ({ ...hop, uid: `saved-${position}` }));
  const thinkers = registry.data ?? [];
  const labelOf = (id?: string | null) =>
    thinkers.find((record) => record.thinkerId === id)?.label ?? id ?? "";
  const policy = fallbacks.data?.policy;
  const set = (next: DraftHop[]) => setDraft(next);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= hops.length || from === to) return;
    const next = [...hops];
    const [hop] = next.splice(from, 1);
    if (hop) next.splice(to, 0, hop);
    set(next);
  };

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "When a thinker does not answer, the chain says what comes next, hop by hop: auto runs it on the next thinker, ask puts one question to you with the cost, never stops there.",
        )}
      </Text>
      {policy ? (
        <div className="flex flex-col">
          <Switch
            checked={policy.neverLeaveEstate}
            label={t("Never leave the estate")}
            description={t(
              "Calls carrying customer data never go to a provider, whatever the chain says.",
            )}
            disabled={!canAdminister || save.isPending}
            onCheckedChange={(on) =>
              save.mutate({ neverLeaveEstate: on }, { onError: notify.fail })
            }
          />
          <Switch
            checked={policy.poolOptIn}
            label={t("Use the estate pool")}
            description={t(
              "Thinkers other workspaces put in the pool are open to yours at low priority.",
            )}
            disabled={!canAdminister || save.isPending}
            onCheckedChange={(on) => save.mutate({ poolOptIn: on }, { onError: notify.fail })}
          />
        </div>
      ) : null}
      <p className="text-small text-text-secondary">{t("Workspace default chain")}</p>
      {hops.length === 0 ? (
        <Empty
          icon={Scales}
          message={t("No chain: a thinker that does not answer is a named failure.")}
        />
      ) : null}
      <Rows>
        {hops.map((hop, index) => (
          <Row key={hop.uid}>
            <div className="flex min-w-0 flex-1 flex-wrap items-end gap-2">
              {canAdminister ? (
                <button
                  type="button"
                  draggable
                  aria-label={t("Drag to reorder")}
                  className="cursor-grab self-center text-text-tertiary"
                  onDragStart={() => setDragged(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (dragged !== null) move(dragged, index);
                    setDragged(null);
                  }}
                >
                  <DotsSixVertical size={16} aria-hidden />
                </button>
              ) : null}
              <span className="text-small text-text-tertiary">{index + 1}</span>
              <Select
                label={t("Thinker")}
                value={hop.thinkerId ?? ""}
                className="min-w-48"
                disabled={!canAdminister}
                onChange={(event) =>
                  set(
                    hops.map((h, i) => (i === index ? { ...h, thinkerId: event.target.value } : h)),
                  )
                }
              >
                <option value="">{t("Choose a thinker")}</option>
                {thinkers.map((record) => (
                  <option key={record.thinkerId} value={record.thinkerId}>
                    {record.label}
                    {record.kind === "provider" ? ` (${t("provider")})` : ""}
                  </option>
                ))}
                {hop.thinkerId && !thinkers.some((record) => record.thinkerId === hop.thinkerId) ? (
                  <option value={hop.thinkerId}>{labelOf(hop.thinkerId)}</option>
                ) : null}
              </Select>
              <Select
                label={t("Then")}
                value={hop.mode}
                disabled={!canAdminister}
                onChange={(event) =>
                  set(
                    hops.map((h, i) =>
                      i === index ? { ...h, mode: event.target.value as FallbackHop["mode"] } : h,
                    ),
                  )
                }
              >
                <option value="auto">{t("auto")}</option>
                <option value="ask">{t("ask")}</option>
                <option value="never">{t("never")}</option>
              </Select>
              <Input
                label={t("$ a day")}
                value={hop.maxUsdPerDay?.toString() ?? ""}
                className="w-24"
                disabled={!canAdminister}
                onChange={(event) =>
                  set(
                    hops.map((h, i) =>
                      i === index ? { ...h, maxUsdPerDay: Number(event.target.value) || null } : h,
                    ),
                  )
                }
              />
            </div>
            {canAdminister ? (
              <>
                <IconButton
                  icon={ArrowUp}
                  size="sm"
                  aria-label={t("Move up")}
                  onClick={() => move(index, index - 1)}
                />
                <IconButton
                  icon={ArrowDown}
                  size="sm"
                  aria-label={t("Move down")}
                  onClick={() => move(index, index + 1)}
                />
                <IconButton
                  icon={Trash}
                  size="sm"
                  aria-label={t("Remove")}
                  onClick={() => set(hops.filter((_, i) => i !== index))}
                />
              </>
            ) : null}
          </Row>
        ))}
      </Rows>
      {canAdminister ? (
        <div className="flex gap-2">
          <Button
            size="sm"
            icon={Plus}
            onClick={() =>
              set([...hops, { thinkerId: "", mode: "auto", uid: crypto.randomUUID() }])
            }
          >
            {t("Add a hop")}
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={draft === null || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  scope: "team",
                  chain: hops.filter((hop) => hop.thinkerId).map(({ uid: _uid, ...hop }) => hop),
                },
                {
                  onSuccess: () => {
                    notify.ok(t("Chain saved"));
                    setDraft(null);
                  },
                  onError: notify.fail,
                },
              )
            }
          >
            {t("Save the chain")}
          </Button>
        </div>
      ) : null}
      {(fallbacks.data?.chains ?? []).filter((chain) => chain.scope !== "team").length > 0 ? (
        <>
          <p className="text-small text-text-secondary">{t("Chains for one thinker or role")}</p>
          <Rows>
            {(fallbacks.data?.chains ?? [])
              .filter((chain) => chain.scope !== "team")
              .map((chain) => (
                <Row key={chain.id}>
                  <p className="min-w-0 flex-1 truncate text-body text-text">
                    {labelOf(chain.scopeId)} → {chain.labels.filter(Boolean).join(" → ")}
                  </p>
                  <Pill>{t(chain.scope)}</Pill>
                </Row>
              ))}
          </Rows>
        </>
      ) : null}
    </div>
  );
}

// ── Usage ──────────────────────────────────────────────────────────────────────

export function UsagePanel() {
  const { t } = useTranslation();
  const [days, setDays] = useState(7);
  const usage = useThinkerUsage(days);

  return (
    <div className="flex flex-col gap-3">
      <BudgetsPanel />
      <div className="flex flex-wrap items-end justify-between gap-2">
        <Text variant="small">
          {t(
            "What ran, per thinker and day — telemetry of the estate's own compute, never a price.",
          )}
        </Text>
        <Select
          label={t("Days")}
          value={String(days)}
          onChange={(event) => setDays(Number(event.target.value))}
        >
          <option value="1">{t("Today")}</option>
          <option value="7">{t("7 days")}</option>
          <option value="30">{t("30 days")}</option>
        </Select>
      </div>
      {usage.isLoading ? <Spinner label={t("Loading")} /> : null}
      {usage.error ? <Text variant="small">{errorText(usage.error)}</Text> : null}
      {usage.data ? (
        <>
          {usage.data.mine.length === 0 ? (
            <Empty icon={Scales} message={t("Nothing ran in this window.")} />
          ) : (
            <Rows>
              {usage.data.mine.map((row) => (
                <Row key={`${row.day}-${row.thinkerId}-${row.grantMode}`}>
                  <span className="w-24 shrink-0 text-small text-text-tertiary">
                    {day(row.day)}
                  </span>
                  <p className="min-w-0 flex-1 truncate text-body text-text">{row.label}</p>
                  <Pill>{t(row.grantMode)}</Pill>
                  <span className="text-small text-text-secondary">
                    {t("{{n}} calls · {{tokens}} tokens", {
                      n: row.requests,
                      tokens: row.tokens.toLocaleString(),
                    })}
                  </span>
                </Row>
              ))}
            </Rows>
          )}
          {usage.data.lent.length > 0 ? (
            <>
              <p className="text-small text-text-secondary">
                {t("What others ran on what you lend")}
              </p>
              <Rows>
                {usage.data.lent.map((row) => (
                  <Row key={`${row.grantId}-${row.day}`}>
                    <span className="w-24 shrink-0 text-small text-text-tertiary">
                      {day(row.day)}
                    </span>
                    <p className="min-w-0 flex-1 truncate text-body text-text">
                      {row.thinkerId}{" "}
                      <span className="text-text-tertiary">· {row.recipient ?? t("a team")}</span>
                    </p>
                    <span className="text-small text-text-secondary">
                      {t("{{n}} calls · {{tokens}} tokens", {
                        n: row.requests,
                        tokens: row.tokens.toLocaleString(),
                      })}
                    </span>
                  </Row>
                ))}
              </Rows>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
